import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Save,
  ChevronDown,
  X,
  Sparkles,
  GitBranch,
  Clock,
  Mail,
  Users,
  Zap,
  Bell,
  Bot,
  Copy,
  ChevronUp,
  MoreVertical,
  Loader2,
  HelpCircle,
  Play,
  Maximize2,
  Settings,
  Triangle,
  CircleDot,
  PlusCircle,
  Linkedin,
  Search,
  Webhook,
  FileText,
  Database,
  RefreshCw,
  MessageSquare,
  CircleStop,
  UserCheck,
  LayoutGrid,
  ArrowDownToLine,
  ArrowRightToLine,
  Spline,
  Minus,
  CornerDownRight,
  Grid3X3,
  Magnet,
  Square,
  BarChart3,
  Shield,
  ClipboardCheck,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import type { Edge } from '@xyflow/react'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/brand/Logo'
import {
  WorkflowCanvas,
  AgentNode,
  HumanReviewNode,
  type AgentNodeConfig,
  type HumanReviewNodeConfig,
  type WorkflowNode,
  type WorkflowNodeType,
  type WorkflowNodeData,
} from '@/components/workflows'
import type { TriggerShapeNodeData } from '@/components/workflows/shapes/TriggerShapeNode'
import type { ConditionShapeNodeData } from '@/components/workflows/shapes/ConditionShapeNode'
import type { ActionShapeNodeData } from '@/components/workflows/shapes/ActionShapeNode'
import type { EndShapeNodeData } from '@/components/workflows/shapes/EndShapeNode'
import {
  serializeWorkflow,
  deserializeWorkflow,
  parseWorkflowDefinition,
  createEmptyWorkflow,
  getAvailableVariables,
} from '@/lib/utils/workflowSerializer'
import { workflowService } from '@/lib/api/services/workflows'
import { agentService } from '@/lib/api/services/agents'

// Default agent configurations matching the system agents
const defaultSystemAgents = [
  { id: 'alex', name: 'Alex', description: 'Content Finder - Semantic search and asset discovery', slug: 'alex', color: '#3B82F6' },
  { id: 'scarlet', name: 'Scarlet', description: 'Brand Guardian - Brand compliance checker', slug: 'scarlet', color: '#EF4444' },
  { id: 'sia', name: 'Sia', description: 'Writing Partner - Creative content writer', slug: 'sia', color: '#8B5CF6' },
  { id: 'olga', name: 'Olga', description: 'Content Strategist - Strategic content advisor', slug: 'olga', color: '#F59E0B' },
  { id: 'dimi', name: 'Dimi', description: 'Document Builder - Document assembly automation', slug: 'dimi', color: '#F97316' },
  { id: 'andrew', name: 'Andrew', description: 'Content Analyst - Data-driven analytics', slug: 'andrew', color: '#14B8A6' },
]

const mockAvailableUsers = [
  { id: 'user-1', name: 'John Smith', email: 'john@example.com' },
  { id: 'user-2', name: 'Jane Doe', email: 'jane@example.com' },
  { id: 'user-3', name: 'Bob Wilson', email: 'bob@example.com' },
]

const mockAvailableRoles = [
  { id: 'role-1', name: 'Manager' },
  { id: 'role-2', name: 'Legal Team' },
  { id: 'role-3', name: 'Finance Team' },
  { id: 'role-4', name: 'Compliance Officer' },
  { id: 'role-5', name: 'Content Team' },
]

// Node library items
interface NodeLibraryItem {
  type: WorkflowNodeType
  icon: React.ComponentType<{ className?: string }>
  label: string
  description: string
  highlighted?: boolean
  triggerType?: string
  actionType?: string
}

const nodeLibraryItems: NodeLibraryItem[] = [
  { type: 'trigger', icon: Zap, label: 'Trigger', description: 'Initiate workflows' },
  { type: 'action', icon: Play, label: 'Action', description: 'Perform actions based on triggers', actionType: 'email' },
  { type: 'action', icon: Clock, label: 'Delay', description: 'Pause the workflow', actionType: 'delay' },
  { type: 'condition', icon: GitBranch, label: 'Conditional', description: 'Branch the workflow' },
  { type: 'condition', icon: Triangle, label: 'Decision', description: 'Route the workflow' },
  { type: 'action', icon: Bell, label: 'Notification', description: 'Send alerts or notifications', actionType: 'notification' },
  { type: 'agent', icon: Bot, label: 'AI Agent', description: 'AI Agent Node', highlighted: true },
]

// Widget library item type
interface WidgetItem {
  type: string
  subType?: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  color: string
}

// Widget library items organized by category
const widgetLibrary: Record<string, { label: string; items: WidgetItem[] }> = {
  triggers: {
    label: 'Triggers',
    items: [
      { type: 'trigger', subType: 'manual', label: 'Manual Start', icon: Play, color: 'bg-blue-500' },
      { type: 'trigger', subType: 'schedule', label: 'Schedule', icon: Clock, color: 'bg-blue-500' },
      { type: 'trigger', subType: 'webhook', label: 'Webhook', icon: Webhook, color: 'bg-blue-500' },
      { type: 'trigger', subType: 'form', label: 'Form', icon: FileText, color: 'bg-blue-500' },
    ],
  },
  ai: {
    label: 'AI & Processing',
    items: [
      { type: 'agent', label: 'AI Agent', icon: Bot, color: 'bg-purple-500' },
      { type: 'humanReview', label: 'Human Review', icon: UserCheck, color: 'bg-cyan-500' },
    ],
  },
  logic: {
    label: 'Logic & Flow',
    items: [
      { type: 'condition', label: 'Condition', icon: GitBranch, color: 'bg-amber-500' },
      { type: 'action', subType: 'delay', label: 'Delay', icon: Clock, color: 'bg-orange-500' },
      { type: 'action', subType: 'loop', label: 'Repeat', icon: RefreshCw, color: 'bg-orange-500' },
    ],
  },
  actions: {
    label: 'Actions',
    items: [
      { type: 'action', subType: 'email', label: 'Send Email', icon: Mail, color: 'bg-emerald-500' },
      { type: 'action', subType: 'webhook', label: 'API Call', icon: Zap, color: 'bg-emerald-500' },
      { type: 'action', subType: 'database', label: 'Database', icon: Database, color: 'bg-emerald-500' },
      { type: 'action', subType: 'document', label: 'Document', icon: FileText, color: 'bg-emerald-500' },
      { type: 'action', subType: 'notification', label: 'Notify', icon: Bell, color: 'bg-emerald-500' },
      { type: 'action', subType: 'sms', label: 'Send SMS', icon: MessageSquare, color: 'bg-emerald-500' },
    ],
  },
  terminal: {
    label: 'Terminal',
    items: [
      { type: 'end', subType: 'success', label: 'Success End', icon: CircleStop, color: 'bg-green-500' },
      { type: 'end', subType: 'failure', label: 'Failure End', icon: CircleStop, color: 'bg-red-500' },
      { type: 'end', label: 'End', icon: CircleStop, color: 'bg-gray-500' },
    ],
  },
}

// Collapsible sections
const collapsibleSections = [
  { id: 'integrations', label: 'Integration nodes' },
  { id: 'data', label: 'Data processing' },
  { id: 'advanced', label: 'Advanced actions' },
]

// Template options for the modal - organized by use case
interface TemplateOption {
  id: string
  icon: React.ComponentType<{ className?: string }>
  name: string
  description: string
  nodeCount?: number
  industry?: string
}

const templateOptions: TemplateOption[] = [
  // Primary template - main application workflow
  { id: 'document-generation', icon: Bot, name: 'Document Generation', description: 'Complete document workflow: Generate → Brand Check → Review → Publish', nodeCount: 8, industry: 'All' },
  // Industry-specific templates
  { id: 'legal-contract-review', icon: GitBranch, name: 'Legal Contract Review', description: 'AI-powered contract analysis with risk assessment and legal team approval', nodeCount: 8, industry: 'Legal' },
  { id: 'finance-report', icon: BarChart3, name: 'Financial Report', description: 'Automated quarterly report generation with compliance checks', nodeCount: 7, industry: 'Finance' },
  { id: 'healthcare-compliance', icon: Shield, name: 'Healthcare Documentation', description: 'HIPAA-compliant document processing with audit trail', nodeCount: 6, industry: 'Healthcare' },
  { id: 'insurance-claims', icon: FileText, name: 'Insurance Claims', description: 'Claims document processing with automated validation', nodeCount: 6, industry: 'Insurance' },
  { id: 'consulting-proposal', icon: Users, name: 'Consulting Proposal', description: 'Client proposal workflow with strategy analysis', nodeCount: 7, industry: 'Consulting' },
  // General purpose
  { id: 'content-approval', icon: ClipboardCheck, name: 'Content Approval', description: 'Multi-level content review and approval workflow', nodeCount: 5, industry: 'All' },
  { id: 'empty', icon: Maximize2, name: 'Empty Canvas', description: 'Start from scratch with a blank workflow' },
]

// Pre-defined workflow templates with nodes and edges
interface WorkflowTemplate {
  nodes: WorkflowNode[]
  edges: Edge[]
}

// Template layout constants
const TEMPLATE_CENTER_X = 400
const TEMPLATE_START_Y = 60
const TEMPLATE_Y_GAP = 140
const TEMPLATE_BRANCH_X_GAP = 280

// Workflow templates using actual system agents (Alex, Scarlet, Sia, Olga, Dimi, Andrew)
const workflowTemplates: Record<string, WorkflowTemplate> = {
  // PRIMARY TEMPLATE - Main document generation workflow using all core agents
  'document-generation': {
    nodes: [
      { id: 'trigger-1', type: 'trigger', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y }, data: { label: 'Document Request', triggerType: 'form', status: 'idle' } },
      { id: 'agent-alex', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP }, data: { label: 'Find Content', agentName: 'Alex', agentSlug: 'alex', status: 'idle' } },
      { id: 'agent-olga', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 2 }, data: { label: 'Content Strategy', agentName: 'Olga', agentSlug: 'olga', status: 'idle' } },
      { id: 'agent-sia', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 3 }, data: { label: 'Write Content', agentName: 'Sia', agentSlug: 'sia', status: 'idle' } },
      { id: 'agent-scarlet', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 4 }, data: { label: 'Brand Check', agentName: 'Scarlet', agentSlug: 'scarlet', status: 'idle' } },
      { id: 'condition-1', type: 'condition', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 5 }, data: { label: 'Brand Compliant?', condition: '{{brandScore}} >= 80', status: 'idle' } },
      { id: 'human-1', type: 'humanReview', position: { x: TEMPLATE_CENTER_X - TEMPLATE_BRANCH_X_GAP / 2, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 6 }, data: { label: 'Content Review', reviewTitle: 'Content Team Approval', status: 'idle' } },
      { id: 'agent-sia-revise', type: 'agent', position: { x: TEMPLATE_CENTER_X + TEMPLATE_BRANCH_X_GAP / 2, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 6 }, data: { label: 'Revise Content', agentName: 'Sia', agentSlug: 'sia', status: 'idle' } },
      { id: 'agent-dimi', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 7 }, data: { label: 'Build Document', agentName: 'Dimi', agentSlug: 'dimi', status: 'idle' } },
      { id: 'action-1', type: 'action', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 8 }, data: { label: 'Save to Content Hub', actionType: 'webhook', status: 'idle' } },
      { id: 'action-2', type: 'action', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 9 }, data: { label: 'Notify Requester', actionType: 'email', status: 'idle' } },
      { id: 'end-1', type: 'end', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 10 }, data: { label: 'Complete', endType: 'success' } },
    ],
    edges: [
      { id: 'e1', source: 'trigger-1', target: 'agent-alex' },
      { id: 'e2', source: 'agent-alex', target: 'agent-olga' },
      { id: 'e3', source: 'agent-olga', target: 'agent-sia' },
      { id: 'e4', source: 'agent-sia', target: 'agent-scarlet' },
      { id: 'e5', source: 'agent-scarlet', target: 'condition-1' },
      { id: 'e6', source: 'condition-1', target: 'human-1', sourceHandle: 'true' },
      { id: 'e7', source: 'condition-1', target: 'agent-sia-revise', sourceHandle: 'false' },
      { id: 'e8', source: 'agent-sia-revise', target: 'agent-scarlet' },
      { id: 'e9', source: 'human-1', target: 'agent-dimi' },
      { id: 'e10', source: 'agent-dimi', target: 'action-1' },
      { id: 'e11', source: 'action-1', target: 'action-2' },
      { id: 'e12', source: 'action-2', target: 'end-1' },
    ],
  },

  // LEGAL INDUSTRY - Contract Review Workflow
  'legal-contract-review': {
    nodes: [
      { id: 'trigger-1', type: 'trigger', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y }, data: { label: 'Contract Uploaded', triggerType: 'form', status: 'idle' } },
      { id: 'agent-alex', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP }, data: { label: 'Find Similar Contracts', agentName: 'Alex', agentSlug: 'alex', status: 'idle' } },
      { id: 'agent-andrew', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 2 }, data: { label: 'Analyze Risk', agentName: 'Andrew', agentSlug: 'andrew', status: 'idle' } },
      { id: 'condition-1', type: 'condition', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 3 }, data: { label: 'High Risk?', condition: '{{riskScore}} > 7', status: 'idle' } },
      { id: 'human-1', type: 'humanReview', position: { x: TEMPLATE_CENTER_X - TEMPLATE_BRANCH_X_GAP / 2, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 4 }, data: { label: 'Senior Associate Review', reviewTitle: 'Legal Team Review', status: 'idle' } },
      { id: 'agent-sia', type: 'agent', position: { x: TEMPLATE_CENTER_X + TEMPLATE_BRANCH_X_GAP / 2, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 4 }, data: { label: 'Generate Summary', agentName: 'Sia', agentSlug: 'sia', status: 'idle' } },
      { id: 'agent-scarlet', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 5 }, data: { label: 'Check Terminology', agentName: 'Scarlet', agentSlug: 'scarlet', status: 'idle' } },
      { id: 'action-1', type: 'action', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 6 }, data: { label: 'Update Matter System', actionType: 'webhook', status: 'idle' } },
      { id: 'action-2', type: 'action', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 7 }, data: { label: 'Notify Partner', actionType: 'email', status: 'idle' } },
      { id: 'end-1', type: 'end', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 8 }, data: { label: 'Review Complete', endType: 'success' } },
    ],
    edges: [
      { id: 'e1', source: 'trigger-1', target: 'agent-alex' },
      { id: 'e2', source: 'agent-alex', target: 'agent-andrew' },
      { id: 'e3', source: 'agent-andrew', target: 'condition-1' },
      { id: 'e4', source: 'condition-1', target: 'human-1', sourceHandle: 'true' },
      { id: 'e5', source: 'condition-1', target: 'agent-sia', sourceHandle: 'false' },
      { id: 'e6', source: 'human-1', target: 'agent-scarlet' },
      { id: 'e7', source: 'agent-sia', target: 'agent-scarlet' },
      { id: 'e8', source: 'agent-scarlet', target: 'action-1' },
      { id: 'e9', source: 'action-1', target: 'action-2' },
      { id: 'e10', source: 'action-2', target: 'end-1' },
    ],
  },

  // FINANCE INDUSTRY - Quarterly Report Workflow
  'finance-report': {
    nodes: [
      { id: 'trigger-1', type: 'trigger', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y }, data: { label: 'Quarter End Schedule', triggerType: 'schedule', status: 'idle' } },
      { id: 'agent-alex', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP }, data: { label: 'Gather Portfolio Data', agentName: 'Alex', agentSlug: 'alex', status: 'idle' } },
      { id: 'agent-andrew', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 2 }, data: { label: 'Analyze Performance', agentName: 'Andrew', agentSlug: 'andrew', status: 'idle' } },
      { id: 'agent-olga', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 3 }, data: { label: 'Investment Strategy', agentName: 'Olga', agentSlug: 'olga', status: 'idle' } },
      { id: 'agent-sia', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 4 }, data: { label: 'Write Commentary', agentName: 'Sia', agentSlug: 'sia', status: 'idle' } },
      { id: 'human-1', type: 'humanReview', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 5 }, data: { label: 'Compliance Review', reviewTitle: 'Compliance Officer Approval', status: 'idle' } },
      { id: 'agent-dimi', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 6 }, data: { label: 'Generate Report', agentName: 'Dimi', agentSlug: 'dimi', status: 'idle' } },
      { id: 'action-1', type: 'action', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 7 }, data: { label: 'Distribute to Investors', actionType: 'email', status: 'idle' } },
      { id: 'end-1', type: 'end', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 8 }, data: { label: 'Report Delivered', endType: 'success' } },
    ],
    edges: [
      { id: 'e1', source: 'trigger-1', target: 'agent-alex' },
      { id: 'e2', source: 'agent-alex', target: 'agent-andrew' },
      { id: 'e3', source: 'agent-andrew', target: 'agent-olga' },
      { id: 'e4', source: 'agent-olga', target: 'agent-sia' },
      { id: 'e5', source: 'agent-sia', target: 'human-1' },
      { id: 'e6', source: 'human-1', target: 'agent-dimi' },
      { id: 'e7', source: 'agent-dimi', target: 'action-1' },
      { id: 'e8', source: 'action-1', target: 'end-1' },
    ],
  },

  // HEALTHCARE INDUSTRY - HIPAA Compliant Documentation
  'healthcare-compliance': {
    nodes: [
      { id: 'trigger-1', type: 'trigger', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y }, data: { label: 'Patient Document', triggerType: 'form', status: 'idle' } },
      { id: 'agent-scarlet', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP }, data: { label: 'PHI Compliance Check', agentName: 'Scarlet', agentSlug: 'scarlet', status: 'idle' } },
      { id: 'condition-1', type: 'condition', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 2 }, data: { label: 'HIPAA Compliant?', condition: '{{complianceScore}} >= 100', status: 'idle' } },
      { id: 'human-1', type: 'humanReview', position: { x: TEMPLATE_CENTER_X - TEMPLATE_BRANCH_X_GAP / 2, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 3 }, data: { label: 'HIPAA Officer Review', reviewTitle: 'Compliance Review', status: 'idle' } },
      { id: 'agent-sia', type: 'agent', position: { x: TEMPLATE_CENTER_X + TEMPLATE_BRANCH_X_GAP / 2, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 3 }, data: { label: 'Redact PHI', agentName: 'Sia', agentSlug: 'sia', status: 'idle' } },
      { id: 'agent-dimi', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 4 }, data: { label: 'Generate Document', agentName: 'Dimi', agentSlug: 'dimi', status: 'idle' } },
      { id: 'action-1', type: 'action', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 5 }, data: { label: 'Update EHR', actionType: 'webhook', status: 'idle' } },
      { id: 'end-1', type: 'end', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 6 }, data: { label: 'Documented', endType: 'success' } },
    ],
    edges: [
      { id: 'e1', source: 'trigger-1', target: 'agent-scarlet' },
      { id: 'e2', source: 'agent-scarlet', target: 'condition-1' },
      { id: 'e3', source: 'condition-1', target: 'human-1', sourceHandle: 'true' },
      { id: 'e4', source: 'condition-1', target: 'agent-sia', sourceHandle: 'false' },
      { id: 'e5', source: 'human-1', target: 'agent-dimi' },
      { id: 'e6', source: 'agent-sia', target: 'agent-scarlet' },
      { id: 'e7', source: 'agent-dimi', target: 'action-1' },
      { id: 'e8', source: 'action-1', target: 'end-1' },
    ],
  },

  // INSURANCE INDUSTRY - Claims Processing
  'insurance-claims': {
    nodes: [
      { id: 'trigger-1', type: 'trigger', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y }, data: { label: 'Claim Submitted', triggerType: 'webhook', status: 'idle' } },
      { id: 'agent-alex', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP }, data: { label: 'Extract Claim Data', agentName: 'Alex', agentSlug: 'alex', status: 'idle' } },
      { id: 'agent-andrew', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 2 }, data: { label: 'Validate Coverage', agentName: 'Andrew', agentSlug: 'andrew', status: 'idle' } },
      { id: 'condition-1', type: 'condition', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 3 }, data: { label: 'Auto-Approve?', condition: '{{claimAmount}} < 1000', status: 'idle' } },
      { id: 'action-1', type: 'action', position: { x: TEMPLATE_CENTER_X - TEMPLATE_BRANCH_X_GAP / 2, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 4 }, data: { label: 'Process Payment', actionType: 'webhook', status: 'idle' } },
      { id: 'human-1', type: 'humanReview', position: { x: TEMPLATE_CENTER_X + TEMPLATE_BRANCH_X_GAP / 2, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 4 }, data: { label: 'Claims Analyst Review', reviewTitle: 'Manual Review Required', status: 'idle' } },
      { id: 'action-2', type: 'action', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 5 }, data: { label: 'Update Policy System', actionType: 'webhook', status: 'idle' } },
      { id: 'action-3', type: 'action', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 6 }, data: { label: 'Notify Policyholder', actionType: 'email', status: 'idle' } },
      { id: 'end-1', type: 'end', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 7 }, data: { label: 'Claim Processed', endType: 'success' } },
    ],
    edges: [
      { id: 'e1', source: 'trigger-1', target: 'agent-alex' },
      { id: 'e2', source: 'agent-alex', target: 'agent-andrew' },
      { id: 'e3', source: 'agent-andrew', target: 'condition-1' },
      { id: 'e4', source: 'condition-1', target: 'action-1', sourceHandle: 'true' },
      { id: 'e5', source: 'condition-1', target: 'human-1', sourceHandle: 'false' },
      { id: 'e6', source: 'action-1', target: 'action-2' },
      { id: 'e7', source: 'human-1', target: 'action-2' },
      { id: 'e8', source: 'action-2', target: 'action-3' },
      { id: 'e9', source: 'action-3', target: 'end-1' },
    ],
  },

  // CONSULTING INDUSTRY - Proposal Workflow
  'consulting-proposal': {
    nodes: [
      { id: 'trigger-1', type: 'trigger', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y }, data: { label: 'New RFP Received', triggerType: 'form', status: 'idle' } },
      { id: 'agent-alex', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP }, data: { label: 'Find Similar Proposals', agentName: 'Alex', agentSlug: 'alex', status: 'idle' } },
      { id: 'agent-olga', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 2 }, data: { label: 'Strategy Recommendations', agentName: 'Olga', agentSlug: 'olga', status: 'idle' } },
      { id: 'agent-sia', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 3 }, data: { label: 'Draft Proposal', agentName: 'Sia', agentSlug: 'sia', status: 'idle' } },
      { id: 'agent-scarlet', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 4 }, data: { label: 'Brand Compliance', agentName: 'Scarlet', agentSlug: 'scarlet', status: 'idle' } },
      { id: 'human-1', type: 'humanReview', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 5 }, data: { label: 'Partner Review', reviewTitle: 'Partner Approval', status: 'idle' } },
      { id: 'agent-dimi', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 6 }, data: { label: 'Build Final Document', agentName: 'Dimi', agentSlug: 'dimi', status: 'idle' } },
      { id: 'action-1', type: 'action', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 7 }, data: { label: 'Submit Proposal', actionType: 'email', status: 'idle' } },
      { id: 'end-1', type: 'end', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 8 }, data: { label: 'Proposal Sent', endType: 'success' } },
    ],
    edges: [
      { id: 'e1', source: 'trigger-1', target: 'agent-alex' },
      { id: 'e2', source: 'agent-alex', target: 'agent-olga' },
      { id: 'e3', source: 'agent-olga', target: 'agent-sia' },
      { id: 'e4', source: 'agent-sia', target: 'agent-scarlet' },
      { id: 'e5', source: 'agent-scarlet', target: 'human-1' },
      { id: 'e6', source: 'human-1', target: 'agent-dimi' },
      { id: 'e7', source: 'agent-dimi', target: 'action-1' },
      { id: 'e8', source: 'action-1', target: 'end-1' },
    ],
  },

  // SIMPLE CONTENT APPROVAL WORKFLOW
  'content-approval': {
    nodes: [
      { id: 'trigger-1', type: 'trigger', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y }, data: { label: 'Submit Content', triggerType: 'manual', status: 'idle' } },
      { id: 'agent-scarlet', type: 'agent', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP }, data: { label: 'Brand Check', agentName: 'Scarlet', agentSlug: 'scarlet', status: 'idle' } },
      { id: 'human-1', type: 'humanReview', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 2 }, data: { label: 'Manager Approval', reviewTitle: 'Manager Review', status: 'idle' } },
      { id: 'condition-1', type: 'condition', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 3 }, data: { label: 'Approved?', condition: '{{approved}} == true', status: 'idle' } },
      { id: 'action-1', type: 'action', position: { x: TEMPLATE_CENTER_X - TEMPLATE_BRANCH_X_GAP / 2, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 4 }, data: { label: 'Publish', actionType: 'webhook', status: 'idle' } },
      { id: 'action-2', type: 'action', position: { x: TEMPLATE_CENTER_X + TEMPLATE_BRANCH_X_GAP / 2, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 4 }, data: { label: 'Request Revisions', actionType: 'email', status: 'idle' } },
      { id: 'end-1', type: 'end', position: { x: TEMPLATE_CENTER_X, y: TEMPLATE_START_Y + TEMPLATE_Y_GAP * 5 }, data: { label: 'Complete', endType: 'success' } },
    ],
    edges: [
      { id: 'e1', source: 'trigger-1', target: 'agent-scarlet' },
      { id: 'e2', source: 'agent-scarlet', target: 'human-1' },
      { id: 'e3', source: 'human-1', target: 'condition-1' },
      { id: 'e4', source: 'condition-1', target: 'action-1', sourceHandle: 'true' },
      { id: 'e5', source: 'condition-1', target: 'action-2', sourceHandle: 'false' },
      { id: 'e6', source: 'action-1', target: 'end-1' },
      { id: 'e7', source: 'action-2', target: 'end-1' },
    ],
  },
}

export default function WorkflowBuilder() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const workflowId = searchParams.get('id')
  const { toast } = useToast()

  const [workflowName, setWorkflowName] = useState('Untitled Workflow')
  const [workflowDescription, setWorkflowDescription] = useState('')
  const [workflowStatus, setWorkflowStatus] = useState<'draft' | 'active' | 'inactive'>('draft')
  const [isTogglingStatus, setIsTogglingStatus] = useState(false)
  const [folderName] = useState('Folder Name')
  const [nodes, setNodes] = useState<WorkflowNode[]>([])
  const [edges, setEdges] = useState<Edge[]>([])
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [rightPanelTab, setRightPanelTab] = useState<'properties' | 'widgets'>('properties')
  const [leftPanelTab, setLeftPanelTab] = useState<'nodes' | 'widgets'>('nodes')
  const [widgetSearchQuery, setWidgetSearchQuery] = useState('')
  const [activeWidgetCategory, setActiveWidgetCategory] = useState<string>('triggers')
  const [expandedSections, setExpandedSections] = useState<string[]>([])
  const [showHelpModal, setShowHelpModal] = useState(false)
  const [showLayoutMenu, setShowLayoutMenu] = useState(false)
  const [showEdgeTypeMenu, setShowEdgeTypeMenu] = useState(false)
  const [showCanvasSettingsMenu, setShowCanvasSettingsMenu] = useState(false)
  const [showWorkflowSettingsModal, setShowWorkflowSettingsModal] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)
  const [edgeType, setEdgeType] = useState<'step' | 'smoothstep' | 'bezier'>('step')
  const [canvasKey, setCanvasKey] = useState(0) // Key to force canvas remount
  // Canvas settings
  const [showGrid, setShowGrid] = useState(true)
  const [snapToGrid, setSnapToGrid] = useState(true)
  const [gridSize, setGridSize] = useState(20)
  const [showPageBoundaries, setShowPageBoundaries] = useState(false)

  // Save notification for canvas popup
  const [saveNotification, setSaveNotification] = useState<{ show: boolean; success: boolean; message: string } | null>(null)

  // Available agents from API
  const [availableAgents, setAvailableAgents] = useState<{ id: string; name: string; description: string; slug?: string }[]>(
    defaultSystemAgents.map(a => ({ id: a.id, name: a.name, description: a.description, slug: a.slug }))
  )

  // Panel resize state
  const [leftPanelWidth, setLeftPanelWidth] = useState(224) // default w-56 = 224px
  const [rightPanelWidth, setRightPanelWidth] = useState(288) // default w-72 = 288px
  const [isResizingLeft, setIsResizingLeft] = useState(false)
  const [isResizingRight, setIsResizingRight] = useState(false)

  // Show template modal for new workflows
  useEffect(() => {
    if (!workflowId) {
      setShowTemplateModal(true)
    }
  }, [workflowId])

  // Load workflow from API or create new
  useEffect(() => {
    const loadWorkflow = async () => {
      if (workflowId) {
        setIsLoading(true)
        try {
          const workflow = await workflowService.getWorkflow(workflowId)
          setWorkflowName(workflow.name)
          setWorkflowDescription(workflow.description || '')
          setWorkflowStatus(workflow.status as 'draft' | 'active' | 'inactive')

          // Load definition if available
          console.log('Workflow loaded:', { name: workflow.name, definitionLength: workflow.definition?.length })
          if (workflow.definition) {
            const definition = parseWorkflowDefinition(workflow.definition)
            console.log('Parsed definition:', definition)
            if (definition) {
              const { nodes: parsedNodes, edges: parsedEdges } = deserializeWorkflow(definition)
              console.log('Deserialized workflow:', { nodesCount: parsedNodes.length, edgesCount: parsedEdges.length })
              setNodes(parsedNodes)
              setEdges(parsedEdges)
              // Force canvas remount with new nodes
              setCanvasKey(prev => prev + 1)
            }
          } else {
            console.log('No definition found in workflow')
          }
        } catch (error) {
          toast({
            title: 'Error',
            description: 'Failed to load workflow',
            variant: 'destructive',
          })
        } finally {
          setIsLoading(false)
        }
      }
    }

    loadWorkflow()
  }, [workflowId, toast])

  // Fetch available agents from API
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const agents = await agentService.getActiveAgents()
        if (agents.length > 0) {
          setAvailableAgents(agents.map(a => ({
            id: a.id,
            name: a.name,
            description: a.description || '',
            slug: a.slug,
          })))
        }
        // Keep default system agents as fallback if API returns empty
      } catch (error) {
        console.error('Failed to fetch agents:', error)
        // Keep default system agents on error
      }
    }
    fetchAgents()
  }, [])

  const selectedNode = selectedNodeId ? nodes.find((n) => n.id === selectedNodeId) : null

  const availableVariables = selectedNodeId
    ? getAvailableVariables(serializeWorkflow(nodes, edges), selectedNodeId)
    : []

  const handleNodesChange = useCallback((newNodes: WorkflowNode[]) => {
    setNodes(newNodes)
  }, [])

  const handleEdgesChange = useCallback((newEdges: Edge[]) => {
    setEdges(newEdges)
  }, [])

  const handleNodeDataChange = useCallback(
    (nodeId: string, newData: Record<string, unknown>) => {
      setNodes((prevNodes) =>
        prevNodes.map((node) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, ...newData } as WorkflowNodeData }
            : node
        )
      )
    },
    []
  )

  const handleDragStart = (event: React.DragEvent, nodeType: WorkflowNodeType, extraData?: Record<string, unknown>) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify({ type: nodeType, ...extraData }))
    event.dataTransfer.effectAllowed = 'move'
  }

  const handleSelectTemplate = (templateId: string) => {
    setShowTemplateModal(false)

    if (templateId === 'empty') {
      // Empty canvas - just a trigger node
      const emptyWorkflow = createEmptyWorkflow()
      const { nodes: initialNodes, edges: initialEdges } = deserializeWorkflow(emptyWorkflow)
      setNodes(initialNodes)
      setEdges(initialEdges)
      setWorkflowName('Untitled Workflow')
      setCanvasKey(prev => prev + 1) // Force canvas remount
    } else {
      // Load the pre-defined template
      const template = workflowTemplates[templateId]
      if (template) {
        setNodes(template.nodes)
        setEdges(template.edges)
        setWorkflowName(templateOptions.find(t => t.id === templateId)?.name || 'Untitled Workflow')
        setCanvasKey(prev => prev + 1) // Force canvas remount

        toast({
          title: 'Template Applied',
          description: `Loaded "${templateOptions.find(t => t.id === templateId)?.name}" with ${template.nodes.length} nodes`,
        })
      }
    }
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      const definition = serializeWorkflow(nodes, edges)
      const definitionJson = JSON.stringify(definition)

      if (workflowId) {
        await workflowService.updateWorkflow(workflowId, {
          name: workflowName,
          description: workflowDescription,
          definition: definitionJson,
          steps: nodes.length,
          saveVersion: false, // Temporarily disabled for debugging
          changeDescription: 'Updated workflow',
        })
      } else {
        const created = await workflowService.createWorkflow({
          name: workflowName,
          description: workflowDescription,
          definition: definitionJson,
        })
        navigate(`/workflows/builder?id=${created.id}`, { replace: true })
      }

      // Show success notification on canvas
      setSaveNotification({ show: true, success: true, message: 'Workflow saved successfully' })
      setTimeout(() => setSaveNotification(null), 3000)
    } catch (error) {
      // Show error notification on canvas
      setSaveNotification({ show: true, success: false, message: 'Failed to save workflow' })
      setTimeout(() => setSaveNotification(null), 4000)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteNode = () => {
    if (selectedNodeId) {
      setNodes(prev => prev.filter(n => n.id !== selectedNodeId))
      setEdges(prev => prev.filter(e => e.source !== selectedNodeId && e.target !== selectedNodeId))
      setSelectedNodeId(null)
    }
  }

  const handleToggleStatus = async () => {
    if (!workflowId) {
      toast({
        title: 'Save First',
        description: 'Please save the workflow before changing its status',
        variant: 'destructive',
      })
      return
    }

    try {
      setIsTogglingStatus(true)
      if (workflowStatus === 'active') {
        await workflowService.deactivateWorkflow(workflowId)
        setWorkflowStatus('inactive')
        toast({
          title: 'Success',
          description: 'Workflow deactivated successfully',
        })
      } else {
        await workflowService.activateWorkflow(workflowId)
        setWorkflowStatus('active')
        toast({
          title: 'Success',
          description: 'Workflow activated successfully',
        })
      }
    } catch (error) {
      console.error('Failed to toggle workflow status:', error)
      toast({
        title: 'Error',
        description: 'Failed to update workflow status',
        variant: 'destructive',
      })
    } finally {
      setIsTogglingStatus(false)
    }
  }

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    )
  }

  // Handle panel resize
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isResizingLeft) {
      const newWidth = Math.max(180, Math.min(400, e.clientX - 12)) // 12px margin
      setLeftPanelWidth(newWidth)
    }
    if (isResizingRight) {
      const newWidth = Math.max(200, Math.min(450, window.innerWidth - e.clientX - 12))
      setRightPanelWidth(newWidth)
    }
  }, [isResizingLeft, isResizingRight])

  const handleMouseUp = useCallback(() => {
    setIsResizingLeft(false)
    setIsResizingRight(false)
  }, [])

  useEffect(() => {
    if (isResizingLeft || isResizingRight) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizingLeft, isResizingRight, handleMouseMove, handleMouseUp])

  // Filter widgets by search query
  const filteredWidgets = useMemo(() => {
    if (!widgetSearchQuery) return widgetLibrary

    const filtered: Record<string, { label: string; items: WidgetItem[] }> = {}

    Object.entries(widgetLibrary).forEach(([key, category]) => {
      filtered[key] = {
        label: category.label,
        items: category.items.filter((item) =>
          item.label.toLowerCase().includes(widgetSearchQuery.toLowerCase())
        ),
      }
    })

    return filtered
  }, [widgetSearchQuery])

  // Auto-arrange workflow nodes with proper branching support
  const handleAutoArrange = useCallback((direction: 'horizontal' | 'vertical') => {
    if (nodes.length === 0) return

    const NODE_WIDTH = 200
    const NODE_HEIGHT = 100
    const HORIZONTAL_GAP = 100
    const VERTICAL_GAP = 80

    // Build adjacency map from edges
    const adjacencyMap = new Map<string, string[]>()
    const incomingMap = new Map<string, string[]>()

    nodes.forEach(n => {
      adjacencyMap.set(n.id, [])
      incomingMap.set(n.id, [])
    })

    edges.forEach(e => {
      const targets = adjacencyMap.get(e.source) || []
      targets.push(e.target)
      adjacencyMap.set(e.source, targets)

      const sources = incomingMap.get(e.target) || []
      sources.push(e.source)
      incomingMap.set(e.target, sources)
    })

    // Find root nodes (nodes with no incoming edges)
    const rootNodes = nodes.filter(n => (incomingMap.get(n.id) || []).length === 0)
    const startNodes = rootNodes.length > 0 ? rootNodes : nodes.slice(0, 1)

    // Assign levels using BFS, tracking branch positions
    const levels = new Map<string, number>()
    const branchIndex = new Map<string, number>() // Track horizontal offset for branches
    const queue: { nodeId: string; level: number; branch: number }[] = startNodes.map(n => ({ nodeId: n.id, level: 0, branch: 0 }))
    const visited = new Set<string>()

    while (queue.length > 0) {
      const { nodeId, level, branch } = queue.shift()!
      if (visited.has(nodeId)) continue
      visited.add(nodeId)
      levels.set(nodeId, level)
      branchIndex.set(nodeId, branch)

      const children = adjacencyMap.get(nodeId) || []
      // Sort children to maintain consistent ordering
      children.forEach((childId, idx) => {
        if (!visited.has(childId)) {
          // If parent has multiple children, assign different branch indices
          const childBranch = children.length > 1 ? idx - (children.length - 1) / 2 : branch
          queue.push({ nodeId: childId, level: level + 1, branch: childBranch })
        }
      })
    }

    // Assign remaining nodes (disconnected)
    nodes.forEach(n => {
      if (!levels.has(n.id)) {
        levels.set(n.id, 0)
        branchIndex.set(n.id, 0)
      }
    })

    // Group nodes by level for centering calculation
    const nodesByLevel = new Map<number, WorkflowNode[]>()
    nodes.forEach(n => {
      const level = levels.get(n.id) || 0
      const existing = nodesByLevel.get(level) || []
      existing.push(n)
      nodesByLevel.set(level, existing)
    })

    // Sort nodes at each level by their branch index
    nodesByLevel.forEach((levelNodes, level) => {
      levelNodes.sort((a, b) => (branchIndex.get(a.id) || 0) - (branchIndex.get(b.id) || 0))
      nodesByLevel.set(level, levelNodes)
    })

    // Calculate canvas center
    const canvasCenter = direction === 'vertical' ? 500 : 300

    // Calculate positions
    const arrangedNodes = nodes.map(n => {
      const level = levels.get(n.id) || 0
      const nodesAtLevel = nodesByLevel.get(level) || []
      const indexAtLevel = nodesAtLevel.findIndex(node => node.id === n.id)
      const countAtLevel = nodesAtLevel.length

      let x: number, y: number

      if (direction === 'vertical') {
        // Top-to-bottom layout (main flow direction)
        y = 80 + level * (NODE_HEIGHT + VERTICAL_GAP)
        // Center nodes horizontally, spread them based on count
        const totalWidth = countAtLevel * (NODE_WIDTH + HORIZONTAL_GAP) - HORIZONTAL_GAP
        const startX = canvasCenter - totalWidth / 2
        x = startX + indexAtLevel * (NODE_WIDTH + HORIZONTAL_GAP)
      } else {
        // Left-to-right layout
        x = 80 + level * (NODE_WIDTH + HORIZONTAL_GAP)
        const totalHeight = countAtLevel * (NODE_HEIGHT + VERTICAL_GAP) - VERTICAL_GAP
        const startY = canvasCenter - totalHeight / 2
        y = startY + indexAtLevel * (NODE_HEIGHT + VERTICAL_GAP)
      }

      return {
        ...n,
        position: { x, y },
      }
    })

    setNodes(arrangedNodes)
    setCanvasKey(prev => prev + 1)
    setShowLayoutMenu(false)

    toast({
      title: 'Layout Applied',
      description: `Workflow arranged ${direction === 'horizontal' ? 'left-to-right' : 'top-to-bottom'}`,
    })
  }, [nodes, edges, toast])

  const renderNodeInspector = () => {
    if (!selectedNode) return null

    const nodeType = selectedNode.type as WorkflowNodeType
    const nodeData = selectedNode.data

    switch (nodeType) {
      case 'agent':
        return (
          <AgentNode
            config={nodeData as unknown as AgentNodeConfig}
            onChange={(newConfig) => handleNodeDataChange(selectedNode.id, newConfig as unknown as Record<string, unknown>)}
            availableAgents={availableAgents}
            availableVariables={availableVariables}
          />
        )
      case 'humanReview':
        return (
          <HumanReviewNode
            config={nodeData as unknown as HumanReviewNodeConfig}
            onChange={(newConfig) => handleNodeDataChange(selectedNode.id, newConfig as unknown as Record<string, unknown>)}
            availableUsers={mockAvailableUsers}
            availableRoles={mockAvailableRoles}
            availableVariables={availableVariables}
          />
        )
      case 'trigger':
        return renderTriggerInspector(nodeData as TriggerShapeNodeData)
      case 'condition':
        return renderConditionInspector(nodeData as ConditionShapeNodeData)
      case 'action':
        return renderActionInspector(nodeData as ActionShapeNodeData)
      case 'end':
        return renderEndInspector(nodeData as EndShapeNodeData)
      default:
        return null
    }
  }

  const renderTriggerInspector = (data: TriggerShapeNodeData) => (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
          <CircleDot className="h-3 w-3 text-amber-500" />
          Trigger Type
        </label>
        <select
          value={data.triggerType}
          onChange={(e) =>
            handleNodeDataChange(selectedNode!.id, {
              triggerType: e.target.value as TriggerShapeNodeData['triggerType'],
            })
          }
          className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
        >
          <option value="manual">Manual</option>
          <option value="form">Form Submission</option>
          <option value="schedule">Schedule</option>
          <option value="webhook">Webhook</option>
        </select>
      </div>
    </div>
  )

  const renderConditionInspector = (data: ConditionShapeNodeData) => (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
          <CircleDot className="h-3 w-3 text-amber-500" />
          Condition Expression
        </label>
        <input
          type="text"
          value={data.condition || ''}
          onChange={(e) =>
            handleNodeDataChange(selectedNode!.id, { condition: e.target.value })
          }
          placeholder="e.g., {{amount}} > 1000"
          className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
        />
      </div>
    </div>
  )

  const renderActionInspector = (data: ActionShapeNodeData) => (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
          <CircleDot className="h-3 w-3 text-amber-500" />
          Delay Type
        </label>
        <select
          value={data.actionType}
          onChange={(e) =>
            handleNodeDataChange(selectedNode!.id, {
              actionType: e.target.value as ActionShapeNodeData['actionType'],
            })
          }
          className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
        >
          <option value="">Select Delay Type...</option>
          <option value="delay">Fixed Delay</option>
          <option value="email">Send Email</option>
          <option value="webhook">Call Webhook</option>
        </select>
      </div>
    </div>
  )

  const renderEndInspector = (data: EndShapeNodeData) => (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
          <CircleDot className="h-3 w-3 text-amber-500" />
          End Type
        </label>
        <select
          value={data.endType || 'default'}
          onChange={(e) =>
            handleNodeDataChange(selectedNode!.id, {
              endType: e.target.value as EndShapeNodeData['endType'],
            })
          }
          className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
        >
          <option value="default">Default</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
        </select>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100 dark:bg-gray-900 fixed inset-0 z-50">
      {/* Top Toolbar */}
      <div className="h-12 flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-4">
        {/* Left Section */}
        <div className="flex items-center gap-1">
          <Link to="/ai-studio/workflows" className="mr-2">
            <Logo variant="icon" size="sm" />
          </Link>

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          </button>

          <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />

          {/* Auto-arrange dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowLayoutMenu(!showLayoutMenu)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-400 text-sm font-medium"
            >
              <LayoutGrid className="h-4 w-4" />
              Arrange
              <ChevronDown className="h-3 w-3" />
            </button>
            {showLayoutMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowLayoutMenu(false)}
                />
                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20 min-w-[160px]">
                  <button
                    onClick={() => handleAutoArrange('horizontal')}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <ArrowRightToLine className="h-4 w-4" />
                    Horizontal (L → R)
                  </button>
                  <button
                    onClick={() => handleAutoArrange('vertical')}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <ArrowDownToLine className="h-4 w-4" />
                    Vertical (T → B)
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Edge type dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowEdgeTypeMenu(!showEdgeTypeMenu)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-400 text-sm font-medium"
            >
              {edgeType === 'step' ? <CornerDownRight className="h-4 w-4" /> : edgeType === 'smoothstep' ? <Spline className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
              Edges
              <ChevronDown className="h-3 w-3" />
            </button>
            {showEdgeTypeMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowEdgeTypeMenu(false)}
                />
                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20 min-w-[140px]">
                  <button
                    onClick={() => { setEdgeType('step'); setShowEdgeTypeMenu(false); setCanvasKey(prev => prev + 1) }}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700",
                      edgeType === 'step' ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-700 dark:text-gray-300'
                    )}
                  >
                    <CornerDownRight className="h-4 w-4" />
                    Step
                  </button>
                  <button
                    onClick={() => { setEdgeType('smoothstep'); setShowEdgeTypeMenu(false); setCanvasKey(prev => prev + 1) }}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700",
                      edgeType === 'smoothstep' ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-700 dark:text-gray-300'
                    )}
                  >
                    <Spline className="h-4 w-4" />
                    Smooth Step
                  </button>
                  <button
                    onClick={() => { setEdgeType('bezier'); setShowEdgeTypeMenu(false); setCanvasKey(prev => prev + 1) }}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700",
                      edgeType === 'bezier' ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-700 dark:text-gray-300'
                    )}
                  >
                    <Minus className="h-4 w-4" />
                    Bezier
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Canvas settings dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowCanvasSettingsMenu(!showCanvasSettingsMenu)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-400 text-sm font-medium"
            >
              <Grid3X3 className="h-4 w-4" />
              Canvas
              <ChevronDown className="h-3 w-3" />
            </button>
            {showCanvasSettingsMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowCanvasSettingsMenu(false)}
                />
                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-20 min-w-[200px]">
                  {/* Show Grid Toggle */}
                  <button
                    onClick={() => setShowGrid(!showGrid)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <div className="flex items-center gap-2">
                      <Grid3X3 className="h-4 w-4" />
                      Show Grid
                    </div>
                    <div className={cn(
                      "w-8 h-4 rounded-full transition-colors",
                      showGrid ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
                    )}>
                      <div className={cn(
                        "w-3 h-3 bg-white rounded-full shadow-sm transition-transform mt-0.5",
                        showGrid ? "translate-x-4.5 ml-0.5" : "translate-x-0.5"
                      )} />
                    </div>
                  </button>

                  {/* Snap to Grid Toggle */}
                  <button
                    onClick={() => setSnapToGrid(!snapToGrid)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <div className="flex items-center gap-2">
                      <Magnet className="h-4 w-4" />
                      Snap to Grid
                    </div>
                    <div className={cn(
                      "w-8 h-4 rounded-full transition-colors",
                      snapToGrid ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
                    )}>
                      <div className={cn(
                        "w-3 h-3 bg-white rounded-full shadow-sm transition-transform mt-0.5",
                        snapToGrid ? "translate-x-4.5 ml-0.5" : "translate-x-0.5"
                      )} />
                    </div>
                  </button>

                  {/* Page Boundaries Toggle */}
                  <button
                    onClick={() => setShowPageBoundaries(!showPageBoundaries)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <div className="flex items-center gap-2">
                      <Square className="h-4 w-4" />
                      Page Boundaries
                    </div>
                    <div className={cn(
                      "w-8 h-4 rounded-full transition-colors",
                      showPageBoundaries ? "bg-blue-500" : "bg-gray-300 dark:bg-gray-600"
                    )}>
                      <div className={cn(
                        "w-3 h-3 bg-white rounded-full shadow-sm transition-transform mt-0.5",
                        showPageBoundaries ? "translate-x-4.5 ml-0.5" : "translate-x-0.5"
                      )} />
                    </div>
                  </button>

                  <div className="border-t border-gray-200 dark:border-gray-700 my-2" />

                  {/* Grid Size */}
                  <div className="px-3 py-2">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 block">
                      Grid Size: {gridSize}px
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="50"
                      value={gridSize}
                      onChange={(e) => setGridSize(Number(e.target.value))}
                      className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Center - Workflow Name (Editable) */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">{folderName}</span>
          <span className="text-gray-300 dark:text-gray-600">/</span>
          {isEditingName ? (
            <input
              type="text"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setIsEditingName(false)
                if (e.key === 'Escape') setIsEditingName(false)
              }}
              className="text-sm font-medium text-gray-900 dark:text-white bg-transparent border-b-2 border-blue-500 outline-none px-1 py-0.5 min-w-[200px]"
              autoFocus
            />
          ) : (
            <button
              onClick={() => setIsEditingName(true)}
              className="flex items-center gap-1 text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Click to edit workflow name"
            >
              {workflowName}
              <ChevronDown className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => setShowWorkflowSettingsModal(true)}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-400 hover:text-gray-600"
            title="Workflow Settings"
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-1">
          <button className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            EN
          </button>
          <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500">
            <GitBranch className="h-4 w-4" />
          </button>
          <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500">
            <Settings className="h-4 w-4" />
          </button>
          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium">
            U
          </div>
          <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500">
            <X className="h-4 w-4" />
          </button>
          <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500">
            <Settings className="h-4 w-4" />
          </button>
          <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500">
            <MoreVertical className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Left Panel - Nodes Library */}
        <div
          className="flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col m-3 mr-0 rounded-xl shadow-sm relative"
          style={{ width: leftPanelWidth }}
        >
          {/* Resize Handle */}
          <div
            className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 transition-colors z-10 group"
            onMouseDown={() => setIsResizingLeft(true)}
          >
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-3 h-8 bg-gray-300 dark:bg-gray-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Nodes Library</span>
            <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-400">
              <Copy className="h-4 w-4" />
            </button>
          </div>

          {/* Tabs */}
          <div className="px-3 py-2">
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setLeftPanelTab('nodes')}
                className={cn(
                  'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                  leftPanelTab === 'nodes'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                )}
              >
                Nodes
              </button>
              <button
                onClick={() => setLeftPanelTab('widgets')}
                className={cn(
                  'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                  leftPanelTab === 'widgets'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                )}
              >
                Widgets
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {leftPanelTab === 'nodes' ? (
              /* Nodes Tab Content */
              <div className="p-2 space-y-1">
                {nodeLibraryItems.map((item, index) => {
                  const Icon = item.icon
                  return (
                    <div
                      key={`${item.type}-${index}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item.type, { triggerType: item.triggerType, actionType: item.actionType })}
                      className={cn(
                        'flex items-center gap-3 p-2.5 rounded-lg border cursor-grab active:cursor-grabbing transition-all',
                        item.highlighted
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      )}
                    >
                      <div
                        className={cn(
                          'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                          item.highlighted
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={cn(
                          'text-sm font-medium truncate',
                          item.highlighted ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'
                        )}>
                          {item.label}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  )
                })}

                {/* Collapsible Sections */}
                <div className="pt-2 space-y-1">
                  {collapsibleSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => toggleSection(section.id)}
                      className="w-full flex items-center justify-between px-2 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg"
                    >
                      <span>{section.label}</span>
                      {expandedSections.includes(section.id) ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Widgets Tab Content */
              <div className="flex flex-col h-full">
                {/* Search */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search widgets..."
                      value={widgetSearchQuery}
                      onChange={(e) => setWidgetSearchQuery(e.target.value)}
                      className="w-full h-9 pl-9 pr-3 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Vertical Tabs + Content */}
                <div className="flex-1 flex overflow-hidden">
                  {/* Vertical Category Tabs */}
                  <div className="w-12 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 py-2 flex flex-col gap-1">
                    {Object.entries(widgetLibrary).map(([key, category]) => {
                      const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
                        triggers: Zap,
                        ai: Bot,
                        logic: GitBranch,
                        actions: Play,
                        terminal: CircleStop,
                      }
                      const CategoryIcon = categoryIcons[key] || Zap
                      const isActive = activeWidgetCategory === key

                      return (
                        <button
                          key={key}
                          onClick={() => setActiveWidgetCategory(key)}
                          title={category.label}
                          className={cn(
                            'mx-1.5 p-2 rounded-lg transition-all flex items-center justify-center',
                            isActive
                              ? 'bg-blue-500 text-white'
                              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                          )}
                        >
                          <CategoryIcon className="h-4 w-4" />
                        </button>
                      )
                    })}
                  </div>

                  {/* Widget Items */}
                  <div className="flex-1 overflow-y-auto p-2">
                    {(() => {
                      const category = filteredWidgets[activeWidgetCategory]
                      if (!category || category.items.length === 0) {
                        return (
                          <div className="flex items-center justify-center h-full text-sm text-gray-400">
                            No widgets found
                          </div>
                        )
                      }

                      return (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
                            {category.label}
                          </p>
                          {category.items.map((item, idx) => {
                            const Icon = item.icon
                            return (
                              <div
                                key={`${item.type}-${item.subType || idx}`}
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData('application/reactflow', JSON.stringify({ type: item.type, subType: item.subType }))
                                  e.dataTransfer.effectAllowed = 'move'
                                }}
                                className={cn(
                                  'flex items-center gap-3 p-2.5 rounded-lg cursor-grab active:cursor-grabbing',
                                  'bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600',
                                  'hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-200 dark:hover:border-gray-500',
                                  'transition-all duration-150 hover:shadow-md'
                                )}
                              >
                                <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', item.color)}>
                                  <Icon className="w-4 h-4 text-white" />
                                </div>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  {item.label}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      )
                    })()}
                  </div>
                </div>

                {/* Footer hint */}
                <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    Drag widgets to canvas
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer Buttons - only show when in nodes tab */}
          {leftPanelTab === 'nodes' && (
            <div className="p-2 border-t border-gray-100 dark:border-gray-700 space-y-1">
              <button
                onClick={() => setShowTemplateModal(true)}
                className="w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                Templates
              </button>
              <button
                onClick={() => setShowHelpModal(true)}
                className="w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                Help & Support
              </button>
            </div>
          )}
        </div>

        {/* Canvas */}
        <div className="flex-1 relative">
          <WorkflowCanvas
            key={canvasKey}
            initialNodes={nodes}
            initialEdges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onNodeSelect={setSelectedNodeId}
            selectedNodeId={selectedNodeId}
            hideWidgetLibrary
            edgeType={edgeType}
            showGrid={showGrid}
            snapToGrid={snapToGrid}
            gridSize={gridSize}
            showPageBoundaries={showPageBoundaries}
          />

          {/* Save Notification Popup */}
          {saveNotification?.show && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className={cn(
                'flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border',
                saveNotification.success
                  ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'
              )}>
                {saveNotification.success ? (
                  <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                    <Save className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                    <X className="h-4 w-4 text-red-600 dark:text-red-400" />
                  </div>
                )}
                <span className={cn(
                  'text-sm font-medium',
                  saveNotification.success
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                )}>
                  {saveNotification.message}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Properties */}
        {selectedNode && (
          <div
            className="flex-shrink-0 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col m-3 ml-0 rounded-xl shadow-sm relative"
            style={{ width: rightPanelWidth }}
          >
            {/* Resize Handle */}
            <div
              className="absolute top-0 left-0 w-1 h-full cursor-col-resize hover:bg-blue-500 transition-colors z-10 group"
              onMouseDown={() => setIsResizingRight(true)}
            >
              <div className="absolute top-1/2 left-0 -translate-y-1/2 w-3 h-8 bg-gray-300 dark:bg-gray-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">Node Properties</span>
                <button className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-400">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {(selectedNode.data as { label?: string }).label || 'Selected Node'}
              </p>
            </div>

            {/* Tabs */}
            <div className="px-4 py-3">
              <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setRightPanelTab('properties')}
                  className={cn(
                    'flex-1 px-4 py-2 text-xs font-medium rounded-md transition-all',
                    rightPanelTab === 'properties'
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  )}
                >
                  Config
                </button>
                <button
                  onClick={() => setRightPanelTab('widgets')}
                  className={cn(
                    'flex-1 px-4 py-2 text-xs font-medium rounded-md transition-all',
                    rightPanelTab === 'widgets'
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  )}
                >
                  Style
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {rightPanelTab === 'properties' ? (
                <div className="space-y-4">
                  {renderNodeInspector()}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Customize the appearance of this node
                  </p>
                  <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                    <PlusCircle className="h-4 w-4" />
                    Add a widget
                  </button>
                </div>
              )}
            </div>

            {/* Delete Button */}
            <div className="p-4 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={handleDeleteNode}
                className="w-full px-4 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                Delete node
              </button>
            </div>
          </div>
        )}

        {/* Properties hint when no node selected */}
        {!selectedNode && (
          <div className="absolute top-3 right-3 z-10">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <Settings className="h-4 w-4 text-gray-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Properties</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Select a node to edit</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Create New Workflow</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Choose a template or start from scratch</p>
                </div>
              </div>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-400"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-5 overflow-y-auto">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                Automate your document and asset workflows with AI-powered templates. Each template includes pre-configured triggers, AI agents, and approval steps.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {templateOptions.map((template) => {
                  const Icon = template.icon
                  const isEmptyCanvas = template.id === 'empty'
                  return (
                    <button
                      key={template.id}
                      onClick={() => handleSelectTemplate(template.id)}
                      className={cn(
                        'flex items-start gap-4 p-4 border rounded-xl hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all text-left group',
                        isEmptyCanvas
                          ? 'border-dashed border-gray-300 dark:border-gray-600 col-span-2'
                          : 'border-gray-200 dark:border-gray-700'
                      )}
                    >
                      <div
                        className={cn(
                          'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors',
                          isEmptyCanvas
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 group-hover:text-blue-600 dark:group-hover:text-blue-400'
                            : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        )}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">{template.name}</span>
                          {template.nodeCount && (
                            <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                              {template.nodeCount} steps
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                          {template.description}
                        </p>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Templates include AI agents for document analysis, content generation, and brand compliance checking
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Workflow Settings Modal */}
      {showWorkflowSettingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Settings className="h-5 w-5 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Workflow Settings</h2>
              </div>
              <button
                onClick={() => setShowWorkflowSettingsModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-400"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-5 space-y-5">
              {/* Workflow Name */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Workflow Name
                </label>
                <input
                  type="text"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter workflow name"
                />
              </div>

              {/* Workflow Description */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <textarea
                  value={workflowDescription}
                  onChange={(e) => setWorkflowDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Describe what this workflow does..."
                />
              </div>

              {/* Workflow Status */}
              {workflowId && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Status
                  </label>
                  <div className="flex items-center gap-3">
                    <span className={cn(
                      'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium',
                      workflowStatus === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                      workflowStatus === 'draft' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                    )}>
                      {workflowStatus === 'active' ? 'Active' : workflowStatus === 'draft' ? 'Draft' : 'Inactive'}
                    </span>
                    {workflowStatus !== 'draft' && (
                      <button
                        onClick={handleToggleStatus}
                        disabled={isTogglingStatus}
                        className={cn(
                          'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-50',
                          workflowStatus === 'active'
                            ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                            : 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20'
                        )}
                      >
                        {isTogglingStatus ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : workflowStatus === 'active' ? (
                          'Deactivate'
                        ) : (
                          'Activate'
                        )}
                      </button>
                    )}
                    {workflowStatus === 'draft' && (
                      <button
                        onClick={handleToggleStatus}
                        disabled={isTogglingStatus}
                        className="px-3 py-1.5 text-sm font-medium text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isTogglingStatus ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Activate'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Workflow Info */}
              {workflowId && (
                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Workflow ID: <span className="font-mono">{workflowId}</span>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Nodes: {nodes.length} | Edges: {edges.length}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowWorkflowSettingsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowWorkflowSettingsModal(false)
                  // Changes are already saved to state, will be persisted on save
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help & Support Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                  <HelpCircle className="h-5 w-5 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Help & Support</h2>
              </div>
              <button
                onClick={() => setShowHelpModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-400"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Support" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">Support Team</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Customer Success</p>
                </div>
                <div className="flex gap-2">
                  <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <Linkedin className="h-4 w-4" />
                    LinkedIn
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <Mail className="h-4 w-4" />
                    Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
